import { useAppDispatch } from '_store';
import { DialogType } from '_store/application/types';
import denmsReducer from '_store/denm/denmsReducers';
import { DenmSourceFilter } from '_store/denm/denmsTypes';
import { causesSelector, loadingSelector, showGeoNetworkingDistanceSelector,subCausesSelector  } from '_store/denm/selectors';
import { defaultIcon, useDenmsForRoadSegment } from '_store/denm/utils';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { hasDefinedPropertyDeep } from '_store/utils';
import { useLocalization } from '@fluent/react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardContent, FormControlLabel, Grid, Switch,Typography } from '@material-ui/core';
import { isFuture } from 'date-fns';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { iconForCause } from '../../icons/utils/denm';
import { EtsiMetaData } from '../Denm/DenmList/Card/Etsi';
import { DenmOptionalDetail } from '../Denm/DenmList/Card/Optional';
import { SincroMetaData } from '../Denm/DenmList/Card/Sincro';
import { RsusMetaData } from '../Device/RsusMetaData';
import EventLayout from './EventLayout';
import styles from './styles';

interface DenmLayoutProps {
  id: string;
  onUpdateDenm?: (idExpanded: string) => void;
  onCloneDenm?: (idExpanded: string) => void;
}

const DenmLayout: FC<DenmLayoutProps> = ({ id, onUpdateDenm, onCloneDenm }) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const { canWriteDenm, blockedActionMode } = useRolePermissions();

  const causes = useSelector(causesSelector);
  const subCauses = useSelector(subCausesSelector);
  const selectedRoadSegmentIds: string[] = useSelector(selectedRoadSegmentIdsSelector);
  const events = useDenmsForRoadSegment(selectedRoadSegmentIds);
  const isDenmLoading = useSelector(loadingSelector);
  const showGeoNetworkingDistance = useSelector(showGeoNetworkingDistanceSelector);
  const [requestedById, setRequestedById] = useState(false);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const onDeleteDenm = () => {
    dispatch(denmsReducer.actions.deleteDENM(id));
  };

  const onDisableDenm = () => {
    dispatch(denmsReducer.actions.disableDENM({ ids: [id], disable: true }));
  };

  const onEnableDenm = () => {
    dispatch(denmsReducer.actions.disableDENM({ ids: [id], disable: false }));
  };

  const handleGeoNetworkingDistanceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(denmsReducer.actions.toggleGeoNetworkingDistance(event.target.checked));
  };

  const event = events.find((denm) => denm.id === id || denm.datexiiId === id);

  useEffect(() => {
    setRequestedById(false);
  }, [id]);

  useEffect(() => {
    if (!event && !isDenmLoading && !requestedById) {
      setRequestedById(true);
      dispatch(denmsReducer.actions.denmRequest({ id }));
    }
  }, [dispatch, event, id, isDenmLoading, requestedById]);

  if (isDenmLoading || (!event && !requestedById)) {
    return <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: 10 }} />;
  }

  if (!event) {
    return (
      <EventLayout
        type={DialogType.DENM}
        active={false}
        disabled={false}
        header={l10n.getString('detail-denm-missing-header')}
        auditResourceId={id}
      >
        {l10n.getString('detail-denm-missing-description-0')}
        <br />
        <strong>{id}</strong>
        <br />
        {l10n.getString('detail-denm-missing-description-1')}
      </EventLayout>
    );
  }

  const subCause = subCauses.find((sc) => sc.id === event?.subCauseId);
  const cause = subCause && causes.find((c) => c.id === subCause.causeId);

  return (
    <EventLayout
      type={DialogType.DENM}
      active={isFuture(new Date(event.expiresAt))}
      disabled={event.disabled && new Date(event.expiresAt) > new Date()}
      header={cause?.description ?? l10n.getString('detail-denm-default-event')}
      date={new Date(event.createdAt)}
      onDelete={event.source === DenmSourceFilter.EMERAS && onDeleteDenm}
      onDisable={event.source === DenmSourceFilter.EMERAS && onDisableDenm}
      onEnable={event.source === DenmSourceFilter.EMERAS && onEnableDenm}
      causeIcon={iconForCause(cause, subCause) ?? defaultIcon}
      auditResourceId={event.id}
      // every source can be updated: non-emeras DENMs open the traces-only mode
      onUpdate={onUpdateDenm ? () => onUpdateDenm(event.id) : undefined}
      onClone={
        event.source === DenmSourceFilter.EMERAS && onCloneDenm
          ? () => onCloneDenm(event.id)
          : undefined
      }
      writeAllowed={canWriteDenm}
      blockedActionMode={blockedActionMode}
      blockedMessage={writeBlockedMessage}
    >
      <CardContent className={classes.detailStyle}>
        <Grid container spacing={1} style={{ marginTop: 10 }}>
          <Grid item xs={12}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-id')} {`\n`}
              <strong>{event.id}</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-cause')} {`\n`}
              <strong>
                {cause ? cause.description : l10n.getString('detail-denm-missing-value-unknown')}
              </strong>
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-subcause')} {`\n`}{' '}
              <strong>
                {subCause
                  ? subCause.description
                  : l10n.getString('detail-denm-missing-value-unknown')}
              </strong>
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.gridVertical}>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-init-date')} {`\n`}{' '}
              <strong>{new Date(event.createdAt).toLocaleString()}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-final-date')} {`\n`}
              <strong>{new Date(event.expiresAt).toLocaleString()}</strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-reference-point')} {`\n`}(
              <strong>
                {event.referencePoint.coordinates[1]}, {event.referencePoint.coordinates[0]})
              </strong>
            </Typography>
            <Typography
              component={'span'}
              variant={'body2'}
              paragraph
              className={classes.textStyle}
            >
              {l10n.getString('forms-denm-source')} {`\n`}
              <strong>
                {event.source === DenmSourceFilter.CONNEX
                  ? l10n.getString('forms-event-source-vehicles')
                  : event.source === DenmSourceFilter.DATEXII
                  ? l10n.getString('forms-event-source-datexii')
                  : event.source}
              </strong>
            </Typography>
            {event.source === DenmSourceFilter.DATEXII && (
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-denm-datexii-id')} {`\n`}
                <strong>{event.datexiiId}</strong>
              </Typography>
            )}

            {event.optional?.geo_networking?.distance_a && (
              <Grid item xs={12} style={{ marginBottom: 10, marginLeft: 5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showGeoNetworkingDistance}
                      onChange={handleGeoNetworkingDistanceToggle}
                      name="showGeoNetworkingDistance"
                      color="primary"
                      size="small"
                    />
                  }
                  label={l10n.getString('forms-denm-show-geonetworking-distance')}
                  classes={{ label: classes.smallLabel }}
                />
              </Grid>
            )}

            <DenmOptionalDetail
              denm={event}
              showManagement={
                event.optional?.management && hasDefinedPropertyDeep(event.optional?.management)
              }
              showSituation={
                event.optional?.situation && hasDefinedPropertyDeep(event.optional?.situation)
              }
              showLocation={
                event.optional?.location && hasDefinedPropertyDeep(event.optional?.location)
              }
              showAlacarte={
                event.optional?.alacarte && hasDefinedPropertyDeep(event.optional?.alacarte)
              }
            />
            {event.metadata?.etsi && <EtsiMetaData etsiData={event.metadata?.etsi} />}
          </Grid>
        </Grid>
        {event.metadata?.sincro && (
          <>
            {l10n.getString('details-metadata')} <br />
            <SincroMetaData sincroData={event.metadata?.sincro} />
          </>
        )}
        {event.metadata?.rsus && event.metadata?.rsus.length > 0 && (
          <>
            {l10n.getString('selected-devices')} <br />
            <RsusMetaData rsus={event.metadata?.rsus} />
          </>
        )}
      </CardContent>
    </EventLayout>
  );
};

export default React.memo(DenmLayout);
